package com.videoanalyzer.web.components.analysis

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.models.ScoreFactor
import com.videoanalyzer.web.models.TitleAnalysis
import com.videoanalyzer.web.models.TitleScores
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Title analysis section components.
 */

@Composable
fun TitleAnalysisCard(analysis: TitleAnalysis) {
    Div({
        classes("card", "fade-in-left", "stagger-1")
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Div({
                    style {
                        width(32.px)
                        height(32.px)
                        borderRadius(6.px)
                        backgroundColor(Color("#27272A"))
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        fontSize(16.px)
                    }
                }) { Text("📝") }

                H3({
                    style {
                        fontSize(15.px)
                        fontWeight(600)
                        color(Color("#FAFAFA"))
                    }
                }) { Text("Title Analysis") }
            }

            ScoreBadge(analysis.overallScore * 10)
        }

        // Original title
        Div({
            style {
                backgroundColor(Color("#27272A"))
                padding(12.px)
                borderRadius(6.px)
                marginBottom(16.px)
            }
        }) {
            P({
                style {
                    fontSize(10.px)
                    color(Color("#71717A"))
                    marginBottom(4.px)
                    fontWeight(500)
                    property("text-transform", "uppercase")
                    letterSpacing(0.5.px)
                }
            }) { Text("Original Title") }
            P({
                style {
                    color(Color("#FAFAFA"))
                    fontWeight(500)
                    fontSize(14.px)
                    lineHeight("1.4")
                }
            }) { Text(analysis.originalTitle.ifBlank { "N/A" }) }
        }

        // Score factors with progress bars
        ScoreFactors(analysis.scores)

        // Improved title suggestion
        if (analysis.improvedTitle.isNotBlank() && analysis.improvedTitle != analysis.originalTitle) {
            Div({
                style {
                    backgroundColor(Color("rgba(34, 197, 94, 0.1)"))
                    padding(12.px)
                    borderRadius(6.px)
                    marginTop(16.px)
                    property("border-left", "3px solid #22C55E")
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                        marginBottom(6.px)
                    }
                }) {
                    Span({ style { fontSize(14.px) } }) { Text("💡") }
                    Span({
                        style {
                            fontSize(11.px)
                            color(Color("#22C55E"))
                            fontWeight(600)
                            property("text-transform", "uppercase")
                            letterSpacing(0.3.px)
                        }
                    }) { Text("Suggested Improvement") }
                }
                P({
                    style {
                        color(Color("#FAFAFA"))
                        fontWeight(500)
                        fontSize(13.px)
                        lineHeight("1.4")
                    }
                }) { Text(analysis.improvedTitle) }
            }
        }

        // Suggestions list
        if (analysis.suggestions.isNotEmpty()) {
            Div({ style { marginTop(16.px) } }) {
                P({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(Color("#A1A1AA"))
                        marginBottom(8.px)
                        property("text-transform", "uppercase")
                        letterSpacing(0.3.px)
                    }
                }) { Text("Tips") }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                    }
                }) {
                    analysis.suggestions.forEach { suggestion ->
                        SuggestionItem(suggestion)
                    }
                }
            }
        }
    }
}

@Composable
fun SuggestionItem(text: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(8.px)
            padding(8.px, 10.px)
            backgroundColor(Color("#27272A"))
            borderRadius(6.px)
        }
    }) {
        Span({
            style {
                color(Color("#0EA5E9"))
                fontSize(12.px)
            }
        }) { Text("→") }
        Span({
            style {
                color(Color("#A1A1AA"))
                fontSize(12.px)
                lineHeight("1.4")
            }
        }) { Text(text) }
    }
}

@Composable
fun ScoreFactors(scores: TitleScores) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
        }
    }) {
        ScoreFactorBar("Curiosity Gap", scores.curiosityGap)
        ScoreFactorBar("Clarity", scores.clarity)
        ScoreFactorBar("Emotional Trigger", scores.emotionalTrigger)
        ScoreFactorBar("Keywords", scores.keywords)
        ScoreFactorBar("Length", scores.length)
    }
}

@Composable
fun ScoreFactorBar(label: String, factor: ScoreFactor) {
    val color = getScoreColor(factor.score * 10)
    val percentage = factor.score * 10

    Div({ style { padding(4.px, 0.px) } }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(4.px)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    color(Color("#A1A1AA"))
                }
            }) { Text(label) }

            Span({
                style {
                    fontFamily("'SF Mono', Monaco, monospace")
                    fontSize(12.px)
                    fontWeight(600)
                    color(Color(color))
                }
            }) { Text("${factor.score}/10") }
        }

        // Progress bar - solid color, no gradient
        Div({ classes("progress-bar") }) {
            Div({
                classes("progress-fill")
                style {
                    width(percentage.percent)
                    backgroundColor(Color(color))
                }
            })
        }
    }
}
